import React, { useState } from "react";
import { Search, Loader2, CheckCircle2, Truck, ExternalLink, Package } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

const TrackOrder = () => {
  const [orderId, setOrderId] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const track = async () => {
    if (!orderId.trim() || !phone.trim()) { toast.error("Enter Order ID and phone number"); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/track", { order_id: orderId, phone });
      setResult(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const currentIdx = result ? result.statuses.indexOf(result.status) : -1;

  return (
    <div className="mx-auto max-w-3xl px-5 sm:px-8 py-12 sm:py-16">
      <div className="text-center mb-10">
        <p className="text-goldsoft text-xs uppercase tracking-[0.3em] mb-2">Where's my order?</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-choco">Track Your Order</h1>
        <p className="text-muted-foreground mt-3">Enter your Order ID and phone number to see the latest status.</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#EFEBE1] p-6 sm:p-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-choco">Order ID</label>
            <Input data-testid="track-order-id" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="NBH-10482" className="mt-1.5 h-12 bg-cream border-[#E5DFD3]" />
          </div>
          <div>
            <label className="text-sm font-medium text-choco">Phone Number</label>
            <Input data-testid="track-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone" className="mt-1.5 h-12 bg-cream border-[#E5DFD3]" />
          </div>
        </div>
        <button data-testid="track-submit" onClick={track} disabled={loading} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark active:scale-95 transition-[background-color,transform] disabled:opacity-60">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />} Track Order
        </button>
      </div>

      {result && (
        <div data-testid="track-result" className="bg-white rounded-2xl border border-[#EFEBE1] p-6 sm:p-8 mt-6">
          <div className="flex flex-wrap justify-between items-center gap-2 pb-5 border-b border-[#EFEBE1]">
            <div>
              <p className="text-sm text-muted-foreground">Order ID</p>
              <p className="font-display text-2xl font-bold text-choco">{result.order_id}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="font-display text-2xl font-bold text-choco">{formatINR(result.total)}</p>
            </div>
          </div>

          <div className="mt-8">
            {result.statuses.map((s, i) => {
              const done = i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${done ? "bg-choco text-cream" : "bg-mutedcream text-muted-foreground"}`}>
                      {done ? <CheckCircle2 size={18} /> : <span className="h-2 w-2 rounded-full bg-muted-foreground" />}
                    </div>
                    {i < result.statuses.length - 1 && <div className={`w-0.5 flex-1 min-h-[28px] ${i < currentIdx ? "bg-choco" : "bg-[#E5DFD3]"}`} />}
                  </div>
                  <div className={`pb-6 ${active ? "" : ""}`}>
                    <p className={`font-semibold ${active ? "text-goldsoft" : done ? "text-choco" : "text-muted-foreground"}`}>{s}</p>
                    {active && <p className="text-xs text-muted-foreground mt-0.5">Current status</p>}
                  </div>
                </div>
              );
            })}
          </div>

          {(result.courier_name || result.tracking_url) && (
            <div className="mt-4 bg-cream rounded-xl p-5">
              <div className="flex items-center gap-2 text-choco font-semibold"><Truck size={18} className="text-goldsoft" /> Shipment</div>
              {result.courier_name && <p className="text-sm mt-2"><span className="text-muted-foreground">Courier: </span>{result.courier_name}</p>}
              {result.awb_number && <p className="text-sm"><span className="text-muted-foreground">AWB: </span>{result.awb_number}</p>}
              {result.tracking_url && (
                <a href={result.tracking_url} target="_blank" rel="noreferrer" data-testid="track-shipment-link" className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-choco text-cream text-sm font-medium px-4 py-2 hover:bg-chocodark transition-colors">
                  Track Shipment <ExternalLink size={14} />
                </a>
              )}
            </div>
          )}

          <div className="mt-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-choco mb-2"><Package size={16} className="text-goldsoft" /> Items</p>
            {result.items.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm py-1">
                <span className="text-chocodark">{it.name} × {it.quantity}</span>
                <span className="font-medium">{formatINR(it.line_total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackOrder;
